import { useCallback, useEffect, useState } from "react";
import {
  fetchFoodBranchLocatorImportBatch,
  fetchFoodBranchLocatorImportBatches,
  fetchFoodBranchLocatorImportStatus,
  postFoodBranchLocatorImportDryRun,
  postFoodBranchLocatorImportRun,
} from "../../../../api/foodApi";
import { CHAIN_BRANCH_LOCATOR_SOURCE_KEYS } from "../../../../constants/foodChainBranchLocatorConstants";
import AdminTableContainer from "../layout/AdminTableContainer";

const SOURCE_OPTIONS = [
  { key: CHAIN_BRANCH_LOCATOR_SOURCE_KEYS.EXITO, brand: "Éxito" },
  { key: CHAIN_BRANCH_LOCATOR_SOURCE_KEYS.CARULLA, brand: "Carulla" },
  { key: CHAIN_BRANCH_LOCATOR_SOURCE_KEYS.JUMBO, brand: "Jumbo" },
  { key: CHAIN_BRANCH_LOCATOR_SOURCE_KEYS.OLIMPICA, brand: "Olímpica" },
  { key: CHAIN_BRANCH_LOCATOR_SOURCE_KEYS.D1, brand: "D1" },
  { key: CHAIN_BRANCH_LOCATOR_SOURCE_KEYS.ARA, brand: "Ara" },
];

const FoodChainBranchImportsPage = ({ onClose }) => {
  const [sourceKey, setSourceKey] = useState(SOURCE_OPTIONS[0].key);
  const [chainBrand, setChainBrand] = useState(SOURCE_OPTIONS[0].brand);
  const [filePath, setFilePath] = useState("docs/food/examples/chain_branch_locator_template.csv");
  const [jsonContent, setJsonContent] = useState("");
  const [useJson, setUseJson] = useState(false);
  const [geocodeMissing, setGeocodeMissing] = useState(true);
  const [allowOutsideBbox, setAllowOutsideBbox] = useState(false);
  const [confirmImport, setConfirmImport] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [importStatus, setImportStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const loadBatches = useCallback(async () => {
    try {
      const payload = await fetchFoodBranchLocatorImportBatches({ limit: 20 });
      setBatches(payload.items || []);
    } catch {
      setBatches([]);
    }
  }, []);

  const loadImportStatus = useCallback(async () => {
    try {
      setImportStatus(await fetchFoodBranchLocatorImportStatus());
    } catch {
      setImportStatus(null);
    }
  }, []);

  useEffect(() => {
    loadBatches();
    loadImportStatus();
  }, [loadBatches, loadImportStatus]);

  useEffect(() => {
    const selected = SOURCE_OPTIONS.find((option) => option.key === sourceKey);
    if (selected) setChainBrand(selected.brand);
  }, [sourceKey]);

  const buildPayload = () => ({
    sourceKey,
    chainBrand,
    geocodeMissing,
    allowOutsideBbox,
    fileName: useJson ? "pasted.json" : filePath,
    ...(useJson
      ? { content: jsonContent, format: "json" }
      : { filePath: filePath.startsWith("/") ? filePath : undefined, content: undefined }),
    ...(!useJson ? { filePath } : {}),
  });

  const handleDryRun = async () => {
    try {
      setRunning(true);
      setErrorMessage("");
      const payload = buildPayload();
      if (useJson) {
        payload.content = jsonContent;
        payload.format = "json";
      } else {
        payload.filePath = filePath;
      }
      setResult(await postFoodBranchLocatorImportDryRun(payload));
    } catch (error) {
      setErrorMessage(error.message || "Dry run failed.");
    } finally {
      setRunning(false);
    }
  };

  const handleImport = async () => {
    if (!confirmImport) {
      setErrorMessage("Check the confirmation box before running a real import.");
      return;
    }
    try {
      setRunning(true);
      setErrorMessage("");
      const payload = buildPayload();
      if (useJson) {
        payload.content = jsonContent;
        payload.format = "json";
      } else {
        payload.filePath = filePath;
      }
      setResult(await postFoodBranchLocatorImportRun({ ...payload, confirm: true }));
      await loadBatches();
      await loadImportStatus();
    } catch (error) {
      setErrorMessage(error.message || "Import failed.");
    } finally {
      setRunning(false);
    }
  };

  const handleSelectBatch = async (batchId) => {
    try {
      setSelectedBatch(await fetchFoodBranchLocatorImportBatch(batchId));
    } catch (error) {
      setErrorMessage(error.message || "Unable to load batch.");
    }
  };

  return (
    <div className="admin-page food-chain-branch-imports-page">
      <header className="admin-page__header">
        <div>
          <h1>Chain Branch Imports</h1>
          <p className="muted">
            Import approved branch locator CSV/JSON files. Dry-run is recommended first. Server reads
            file paths from the repo for CLI-style imports.
          </p>
        </div>
        {onClose ? (
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Back
          </button>
        ) : null}
      </header>

      <section className="panel-card">
        <div className="food-normalization-page__control-row">
          <label>
            Source
            <select value={sourceKey} onChange={(e) => setSourceKey(e.target.value)}>
              {SOURCE_OPTIONS.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.key}
                </option>
              ))}
            </select>
          </label>
          <label>
            Chain brand
            <input value={chainBrand} onChange={(e) => setChainBrand(e.target.value)} />
          </label>
          <label className="food-comparison-filters__checkbox">
            <input type="checkbox" checked={useJson} onChange={(e) => setUseJson(e.target.checked)} />
            Paste JSON
          </label>
          <label className="food-comparison-filters__checkbox">
            <input
              type="checkbox"
              checked={geocodeMissing}
              onChange={(e) => setGeocodeMissing(e.target.checked)}
            />
            Geocode missing
          </label>
          <label className="food-comparison-filters__checkbox">
            <input
              type="checkbox"
              checked={allowOutsideBbox}
              onChange={(e) => setAllowOutsideBbox(e.target.checked)}
            />
            Allow outside bbox
          </label>
        </div>

        {useJson ? (
          <label>
            JSON content
            <textarea
              rows={8}
              value={jsonContent}
              onChange={(e) => setJsonContent(e.target.value)}
              placeholder='{"branches":[...]}'
            />
          </label>
        ) : (
          <label>
            File path (server-side)
            <input value={filePath} onChange={(e) => setFilePath(e.target.value)} />
          </label>
        )}

        <div className="food-normalization-page__control-row">
          <button type="button" className="btn btn-secondary" disabled={running} onClick={handleDryRun}>
            {running ? "Running…" : "Dry run"}
          </button>
          <label className="food-comparison-filters__checkbox">
            <input
              type="checkbox"
              checked={confirmImport}
              onChange={(e) => setConfirmImport(e.target.checked)}
            />
            Confirm real import
          </label>
          <button type="button" className="btn btn--primary" disabled={running} onClick={handleImport}>
            Run import
          </button>
        </div>
      </section>

      {errorMessage ? <p className="form-error">{errorMessage}</p> : null}

      {importStatus?.chains?.length ? (
        <section className="panel-card">
          <h2>Per-chain import status</h2>
          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Chain</th>
                  <th>Source status</th>
                  <th>Last import</th>
                  <th>Rows</th>
                  <th>Needs review</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {importStatus.chains.map((chain) => (
                  <tr key={chain.sourceKey}>
                    <td>{chain.chainBrand}</td>
                    <td>{chain.sourceStatus}</td>
                    <td>{chain.lastImportAt || "—"}</td>
                    <td>{chain.totalChainLocatorRows}</td>
                    <td>{chain.needsReviewCount}</td>
                    <td>{chain.staleStatus}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        </section>
      ) : null}

      {result ? (
        <section className="panel-card">
          <h2>Import summary</h2>
          <ul>
            <li>Dry run: {String(result.dryRun)}</li>
            <li>Rows: {result.rowCount}</li>
            <li>Valid: {result.validRowCount}</li>
            <li>Insert candidates: {result.insertedCount ?? result.insertCandidates}</li>
            <li>Update candidates: {result.updatedCount ?? result.updateCandidates}</li>
            <li>Failed: {result.failedCount}</li>
            <li>Needs review: {result.needsReviewCount}</li>
          </ul>
          <pre className="food-normalization-page__summary">{JSON.stringify(result, null, 2)}</pre>
        </section>
      ) : null}

      <section className="panel-card">
        <h2>Import batch history</h2>
        <AdminTableContainer>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Imported at</th>
                <th>Source</th>
                <th>File</th>
                <th>Status</th>
                <th>Inserted</th>
                <th>Updated</th>
                <th>Failed</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((batch) => (
                <tr key={batch.id}>
                  <td>{batch.importedAt}</td>
                  <td>{batch.sourceKey}</td>
                  <td>{batch.fileName}</td>
                  <td>{batch.status}</td>
                  <td>{batch.insertedCount}</td>
                  <td>{batch.updatedCount}</td>
                  <td>{batch.failedCount}</td>
                  <td>
                    <button type="button" className="btn btn--ghost" onClick={() => handleSelectBatch(batch.id)}>
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>
      </section>

      {selectedBatch ? (
        <section className="panel-card">
          <h2>Batch detail</h2>
          <pre className="food-normalization-page__summary">
            {JSON.stringify(selectedBatch, null, 2)}
          </pre>
        </section>
      ) : null}
    </div>
  );
};

export default FoodChainBranchImportsPage;
